// src/components/SalesSalesTable.tsx
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { t } from 'i18next';
import moment from 'moment';
import React, { useState, useRef, useEffect } from 'react';
import { FaEdit } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import { useToast } from '../../core/contexts/toast';
import { ModeloSopSales } from '../../core/models/sopSales';
import { FiltroSopSales, RepositorioSopSales } from '../../core/repositories/sopSalesRepositorio';
import { formatarPorcentagem, formatLargeNumber, isNumeric } from '../../core/utils/funcoes';
import { useAutenticação } from '../../core/contexts/auth';
import { Modal } from 'react-bootstrap';
import SopLoadingAnimation from '../../components/sopLoadingAnimation';
import ModalEditForecastValue from './ModalEditForecastValue';


interface Props {
  lista: ModeloSopSales[];
  setLista: React.Dispatch<React.SetStateAction<ModeloSopSales[]>>;
  activeTab: string;
  simulation?: boolean;
  filtro: FiltroSopSales;
  salvarValor: () => void;

}

const SopSalesTable: React.FC<Props> = (props) => {
  const gridApi = useRef<any>(null); // Referência para a API da grid
  const { mostrarToast } = useToast();
  const [showCarregando, setShowCarregando] = useState(false);
  const [loading, setLoading] = useState(false);
  const { usuario } = useAutenticação()
  const [showModal, setShowModal] = useState(false);
  const [linha, setLinha] = useState<ModeloSopSales>();



  useEffect(() => {
    if (gridApi.current && props.activeTab === "table") {
      setTimeout(() => {
        gridApi.current.sizeColumnsToFit();
      }, 0);
    }
  }, [props.activeTab]);


  const columnDefs: ColDef<ModeloSopSales>[] = [
    { headerName: 'Business Unit', field: 'des_business_unit', minWidth: 100, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={false} porcetagem={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Month/Year', field: 'des_time', minWidth: 100, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={false} porcetagem={false} date={true} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Region', field: 'des_region', minWidth: 80, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={true} porcetagem={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Product Family', field: 'des_product_family', minWidth: 150, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={false} porcetagem={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Product', field: 'des_product', minWidth: 100, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={true} porcetagem={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Sales Real (units)', field: 'num_sales_real_units', minWidth: 100, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={false} porcetagem={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Sales Forecast (units)', field: 'num_sales_forecast_units', minWidth: 100, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={false} porcetagem={false} attr={params.colDef.field} linha={params.data} /> },
    { headerName: 'Sales Budget (units)', field: 'num_sales_budget_units', minWidth: 100, cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={false} porcetagem={false} attr={params.colDef.field} linha={params.data} /> },
    {
      headerName: 'Sales Real (USD)',
      field: 'num_sales_real_usd',
      minWidth: 100,
      cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={true} porcetagem={false} attr={params.colDef.field} linha={params.data} />,
    },
    {
      headerName: 'Sales Forecast (USD)',
      field: 'num_sales_forecast_usd',
      minWidth: 100,
      cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={true} porcetagem={false} attr={params.colDef.field} linha={params.data} />,

    },
    {
      headerName: 'Sales Budget (USD)',
      field: 'num_sales_budget_usd',
      minWidth: 100,
      cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={true} porcetagem={false} attr={params.colDef.field} linha={params.data} />,

    },
    { headerName: 'Forecast Accuracy', field: 'num_forecast_accuracy', minWidth: 100 },
    { headerName: 'YOY', field: 'num_yoy', minWidth: 80 },
    {
      headerName: 'Selling Price Real (USD/unit)',
      field: 'num_selling_price_real',
      minWidth: 100,
      cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={true} porcetagem={false} attr={params.colDef.field} linha={params.data} />,

    },
    {
      headerName: 'Selling Price Forecast (USD/unit)',
      field: 'num_selling_price_forecast',
      minWidth: 120,
      cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={true} porcetagem={false} attr={params.colDef.field} linha={params.data} />,

    },
    {
      headerName: 'Selling Price Budget (USD/unit)',
      field: 'num_selling_price_budget',
      minWidth: 120,
      cellRenderer: (params: any) => <CustomSkeleton value={params.value} money={true} porcetagem={false} attr={params.colDef.field} linha={params.data} />,
    },
    // {
    //   headerName: 'Suggested Price AI',
    //   field: 'suggested_price_ai',
    //   minWidth: 120,
    //  
    // }
  ];

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
    gridApi.current.sizeColumnsToFit(); // Ajusta as colunas para caber no espaço disponível
  };

  const CustomSkeleton = (propsE: { value: any; money?: boolean; date?: boolean; porcetagem?: boolean, attr?: string, linha?: ModeloSopSales }) => {
    return (
      <>

        {loading ? (
          <Skeleton className="width-100" />
        ) :
          (<label className={'text-capitalize' + (propsE.linha?.num_sales_new ? " text-success" : "")}>
            {(localStorage.getItem("editando") && (typeof propsE.value === "number" || propsE.date)) && <label onClick={() => { atualizarValor(propsE.attr || "", propsE.linha || new ModeloSopSales()) }} className="cursor-pointer">&nbsp;<FaEdit /></label>}
            {
              propsE.value ? propsE.date ? (
                moment(propsE.value).format("MMM-YYYY")
              ) : typeof propsE.value === "number" ? (
                propsE.porcetagem ?
                  formatarPorcentagem(propsE.value / 100, 2)
                  :

                  formatLargeNumber(
                    (props.simulation && propsE.attr == "num_sales_forecast_units" && propsE.linha?.num_sales_new) ?
                      propsE.linha?.num_sales_new : propsE.value
                    , propsE.money ? t("components.R$") : "")

                // (
                //   ( props.simulation && propsE.attr == "num_sales_forecast_units" ) ? 
                //   propsE.linha?.num_sales_new  + "AAA" :propsE.value 
                //  )

              ) : (
                propsE.value
              ) : "-"}
            {(props.simulation && propsE.attr == "num_sales_forecast_units" &&
              (typeof propsE.value === "number" || propsE.date)) && <label onClick={() => {
                simular(propsE.attr || "", propsE.linha || new ModeloSopSales())
              }} className="cursor-pointer">&nbsp;<FaEdit /></label>}
          </label>)}
      </>
    );
  };

  const getRowStyle = (params: any) => {
    const data: ModeloSopSales = params.data;
    const currentMonth = moment().format('YYYY-MM');
    const rowMonth = moment(data.des_time).format('YYYY-MM');

    if (currentMonth === rowMonth) {
      return { backgroundColor: '#e6f7ff' }; // azul claro
    }

    return undefined;
  };

  async function atualizarValor(atributo: string, linha: ModeloSopSales) {

    const valor = prompt(`Por favor, insira um valor ${atributo} (${(linha as any)[atributo]}):`);
    if (valor) {
      const _centro = new RepositorioSopSales();
      _centro.atualizarValor(atributo, valor, linha).then((atualizadas) => {
        if (atualizadas) {
          var encontra = props.lista.find(x => x.cod_tela_sop_sales === linha.cod_tela_sop_sales);
          if (encontra) (encontra as any)[atributo] = isNumeric(valor) ? Number.parseFloat(valor) : valor;
          props.setLista([...props.lista]);
          // rowData.find(x => x.cod_bandeira === linha.cod_bandeira)?.[atributo] = valor;
        }
        mostrarToast({
          titulo: "Sucesso",
          mensagem: `${atualizadas} linha(s) atualizada(s)`,
          tipo: "success",
        });
      }).catch(err => {
        mostrarToast({
          titulo: "Erro",
          mensagem: t("pages.Aconteceu algo inesperado, por favor tente novamente mais tarde!"),
          tipo: "error",
        });
      });
    }
  }

  async function simular(atributo: string, linha: ModeloSopSales) {

    //pegar a modal ja aberta e adicionar a class "modal-backdrop"
    const modal = document.querySelector('.modal');
    if (modal) {
      modal.classList.add('modal-backdrop');
    }
    setLinha(linha);
    setShowModal(true);
  }
  return (
    <div className="ag-theme-alpine" >
      <div>
        <AgGridReact<ModeloSopSales>
          rowData={props.lista}
          columnDefs={columnDefs}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
            headerClass: 'wrap-header',
            cellClass: 'numeric-cell'
          }}
          pagination={true}
          getRowStyle={(params) => getRowStyle(params)}
          paginationPageSize={20}
          domLayout="autoHeight"
          onGridReady={onGridReady} // Chamando a função quando a grid estiver pronta
        />
      </div>

      <Modal show={showCarregando} onHide={() => setShowCarregando(false)} centered className='modal-transparent'>
        <Modal.Body className='border-0'>
          <SopLoadingAnimation type="IA" />
        </Modal.Body>
      </Modal>

      <ModalEditForecastValue showModal={showModal}
        salvarValor={(newValue) => {
          props.salvarValor;
          const modal = document.getElementsByClassName('modal');
          if (modal.length > 0) {
            for (let i = 0; i < modal.length; i++) {
              modal[i].classList.remove('modal-backdrop');
            }
          }
          if (linha)
            linha.num_sales_new = newValue
          // props.lista[modalInfo?.index || 0].num_sales_new = newValue || 0;
          props.setLista([...props.lista]);
        }}
        // label={linha?.des_product}
        // currentValue={modalInfo?.currentValue}
        // currentValueDestaque={modalInfo?.currentValueDestaque}
        linha={linha}
        filtro={props.filtro}
        setShowModal={(show) => {
          setShowModal(show);
          const modal = document.getElementsByClassName('modal');
          if (modal.length > 0) {
            for (let i = 0; i < modal.length; i++) {
              modal[i].classList.remove('modal-backdrop');
            }
          }
        }}
      />


    </div>

  );
};

export default SopSalesTable;